import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import appFunctionAPI from '../../../../lib/commons/appFunction'
import { WbsItemTypeVO } from '../../../../domain/value-object/WbsItemTypeVO'
import { APPLICATION_FUNCTION_EXTERNAL_ID } from '../..'
import { useSelector } from 'react-redux'
import { AllState } from '../../../../store'

export type FunctionPropertyConfigurationState = {
  initialized: boolean
  configurations: FunctionPropertyConfigurationMapByExternalId
}
export type FunctionPropertyConfiguration = {
  externalId: string
  name: string | undefined
  propertyLayout: string | undefined
  parentProperty: string | undefined
  required: boolean | undefined
  editable: boolean | undefined
  hidden: boolean | undefined
}
export type FunctionPropertyConfigurationMapByExternalId = {
  [externalId: string]: FunctionPropertyConfiguration
}

export const useFunctionPropertyConfiguration = (
  projectUuid: string,
  wbsItemType: WbsItemTypeVO, // TODO Not pass "functionUuid" from caller because "functionUuid" to get configurations is different from that to get entity extensions.
  ticketType: string | undefined
): FunctionPropertyConfigurationState => {
  const [configurations, setConfigurations] =
    useState<FunctionPropertyConfigurationMapByExternalId>({})
  const [initialized, setInitialized] = useState<boolean>(false)
  const fetching = useRef(false)
  const functions = useSelector(
    (state: AllState) => state.appFunction.functions
  )
  const functionUuid = useMemo(() => {
    let functionExternalId = APPLICATION_FUNCTION_EXTERNAL_ID.WBS_ITEM
    if (ticketType) {
      functionExternalId = APPLICATION_FUNCTION_EXTERNAL_ID.TICKET
    } else if (wbsItemType.isTask()) {
      /**
       * TODO Use APPLICATION_FUNCTION_EXTERNAL_ID.WBS_ITEM if the type is WBS type.
       * Now Use each external ids to apply function_property_configurations and entity_extensions.
       */
      functionExternalId = APPLICATION_FUNCTION_EXTERNAL_ID.TASK
    } else if (wbsItemType.isDeliverable()) {
      functionExternalId = APPLICATION_FUNCTION_EXTERNAL_ID.DELIVERABLE
    } else if (wbsItemType.isDeliverableList()) {
      functionExternalId = APPLICATION_FUNCTION_EXTERNAL_ID.DELIVERABLE_LIST
    } else if (wbsItemType.isProcess() || wbsItemType.isWorkgroup()) {
      functionExternalId = APPLICATION_FUNCTION_EXTERNAL_ID.PROCESS
    }
    return functions.find(f => f.externalId === functionExternalId)?.uuid || ''
  }, [functions, wbsItemType, ticketType])

  const fetch = useCallback(async () => {
    if (fetching.current) return

    fetching.current = true
    const groupKeys = [projectUuid]
    if (ticketType) {
      groupKeys.push(ticketType)
    }

    const response = await appFunctionAPI.getPropertyConfiguraitons(
      functionUuid,
      groupKeys
    )
    const configurationMapByExternalId: FunctionPropertyConfigurationMapByExternalId =
      {}
    response.json.forEach(v => {
      const config: FunctionPropertyConfiguration = {
        externalId: removePrefixFromExternalId(v.externalId),
        name: v.name || undefined,
        propertyLayout: v.propertyLayout || undefined,
        parentProperty: v.parentProperty
          ? removePrefixFromExternalId(v.parentProperty)
          : undefined,
        required: v.requiredIf
          ? v.requiredIf === 'TRUE' || v.requiredIf === 'true'
          : undefined,
        editable: undefined,
        hidden: undefined,
      }
      if (v.editableIfC) {
        config.editable = v.editableIfC === 'true' || v.editableIfC === 'TRUE'
      }
      if (v.editableIfU) {
        config.editable = v.editableIfU === 'true' || v.editableIfU === 'TRUE'
      }
      // if (v.hiddenIfC) {
      //   config.hidden = v.hiddenIfC === 'true' || v.hiddenIfC === 'TRUE'
      // }
      if (v.hiddenIfU) {
        config.hidden = v.hiddenIfU === 'true' || v.hiddenIfU === 'TRUE'
      }

      configurationMapByExternalId[config.externalId] = config
    })

    setConfigurations(configurationMapByExternalId)
    setInitialized(true)
    fetching.current = false
  }, [functionUuid, projectUuid, ticketType])

  useEffect(() => {
    fetch()
  }, [fetch])

  const state = useMemo(
    () => ({ initialized, configurations }),
    [initialized, configurations]
  )

  return state
}

const removePrefixFromExternalId = (externalId: string): string => {
  const parts = externalId.split('.')
  if (
    'process' === parts[0] ||
    'deliverableList' === parts[0] ||
    'deliverable' === parts[0] ||
    'task' === parts[0] ||
    'ticket' === parts[0]
  ) {
    return parts[parts.length - 1]
  }
  return externalId
}
